import React from "react";

import {
  Document,
  Image,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";
import { format } from "date-fns";

import { MealKind } from "@/gql/graphql";
import Logo from "@assets/images/nutricoach-logo.png";

import MealRatingReportPDFItem from "./MealRatingReportPDFItem";
import { RatingReport } from "../../utils/types";

interface MealRatingReportPDFProps {
  data: Record<MealKind, RatingReport[]>[];
  startDate: Date;
  endDate: Date;
}

const MealRatingReportPDF: React.FC<MealRatingReportPDFProps> = ({
  data,
  startDate,
  endDate,
}) => {
  const formattedStartDate = format(startDate, "dd MMM, yyyy");
  const formattedEndDate = format(endDate, "dd MMM, yyyy");

  return (
    <Document>
      <Page size="A4" wrap={data.length < 9} style={styles.page}>
        <View style={styles.headerContainer}>
          <Image src={Logo} style={styles.logo} />
        </View>
        <View style={styles.contentContainer}>
          <Text style={styles.title}>Meal Rating Report</Text>
          <View style={styles.h4} />
          <Text
            style={styles.subtitle}
          >{`${formattedStartDate} - ${formattedEndDate}`}</Text>
          <View style={styles.h16} />
          <View style={styles.mealTypesContainer}>
            {data.map((item, index) => {
              const isOddAndLastItem =
                data.length % 2 === 1 && data.length - 1 === index;

              return (
                <MealRatingReportPDFItem
                  key={index}
                  item={item}
                  width={!isOddAndLastItem ? undefined : "100%"}
                />
              );
            })}
          </View>
        </View>
        <View
          style={
            data.length < 9
              ? styles.absoluteFooterContainer
              : styles.footerContainer
          }
        >
          <Text style={styles.footerTitle}>
            Powered by Nutricoach. Copyright © 2024. All Rights Reserved
          </Text>
          <View style={styles.h4} />
          <Text style={styles.footerSubtitle}>
            NutriCoach does not own any contents of this program. The program
            and its contents are created and owned by the client`s personal
            nutrition professional. The program has been created specifically
            for the intended client of the personal nutrition professional.
            Nutricoach, Inc. will not be liable for consequences of any kind as
            a result of using the recommendations in this program.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default MealRatingReportPDF;

const styles = StyleSheet.create({
  page: {
    height: "100%",
    width: "100%",
  },
  headerContainer: {
    backgroundColor: "#DBFFF8",
    paddingHorizontal: 8,
    paddingVertical: 12,
  },
  logo: {
    width: "120px",
    height: "40px",
  },
  contentContainer: {
    paddingVertical: 16,
    paddingHorizontal: 24,
  },
  title: {
    color: "#363E45",
    fontFamily: "Mulish",
    fontSize: 24,
    fontWeight: 700,
  },
  h4: {
    height: 4,
  },
  subtitle: {
    fontFamily: "Mulish",
    fontSize: 10,
    fontWeight: 600,
  },
  h16: {
    height: 16,
  },
  mealTypesContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  footerContainer: {
    paddingBottom: 16,
    paddingHorizontal: 24,
  },
  absoluteFooterContainer: {
    position: "absolute",
    bottom: 24,
    left: 16,
    right: 16,
  },
  footerTitle: {
    fontFamily: "Mulish",
    fontSize: 8,
    fontWeight: 700,
  },
  footerSubtitle: {
    color: "#7D8797",
    fontFamily: "Mulish",
    fontSize: 6,
    fontWeight: 700,
  },
});
